import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

// Product and Price IDs from Stripe.
//
// SOLO_ARTIST is the original $19/mo Solo Pro tier.
// SOLO_BOOKER is the $9/mo booking-only tier added for Phase 2 of
// the simplification sprint (Jeff Zalesak feedback May 2026). Solo
// Booker users get a tighter capability set defined in permissions.ts
// SOLO_BOOKER_DEFAULTS.
export const SOLO_ARTIST = {
  PRODUCT_ID: "prod_UHhj1sT45L9QF3",
  PRICE_ID: "price_1TJ8K98UGolkm4dxVddbFFiD", // $19/month
  LEGACY_PRODUCT_IDS: ["prod_U3vJWTq6qgy6oN", "prod_U3vUG04KE6Waf8", "prod_Ts51t8vc1UM34A"],
  // EUR product IDs for Spain
  EUR_PRODUCT_ID: "prod_UIE344ScjsHYrz",
  EUR_PRODUCT_ANNUAL_ID: "prod_UIE3t4RnSZYT34",
} as const;

export const SOLO_BOOKER = {
  PRODUCT_ID: "prod_UWM29YJtnZDo4w",
  PRICE_ID: "price_1TXJJz8UGolkm4dxXXe9zvsD", // $9/month
  LOOKUP_KEY: "solo_booker_monthly",
  // Annual + EU pricing placeholders — fill in once the matching
  // Stripe Prices exist. Until then, attempts to use these IDs will
  // fail loudly at the edge-function level rather than silently.
  PRICE_ID_ANNUAL: "price_1TXKDC8UGolkm4dxB28zag20", // $90/yr (~$7.50/mo, save $18)
  PRICE_ID_EUR_MONTHLY: "TODO_solo_booker_eur_monthly", // €9/mo
  PRICE_ID_EUR_ANNUAL: "TODO_solo_booker_eur_annual", // €90/yr
  PRICE_ID_GBP_MONTHLY: "TODO_solo_booker_gbp_monthly", // £8/mo
  PRICE_ID_GBP_ANNUAL: "TODO_solo_booker_gbp_annual", // £80/yr
} as const;

// Solo Pro Plus tier ($29/mo) -- Solo Pro + "guest chair" capability.
// Target: solo artists hosting visiting / guest artists who currently
// overpay Booksy/Vagaro on per-seat charges. Stripe products are
// not yet created -- the constants below are placeholders that
// useSoloArtistSubscription will gracefully treat as "no match" until
// real IDs replace the TODO_ strings.
export const SOLO_PRO_PLUS = {
  PRODUCT_ID: "prod_UWMzWWK7QkCByv",
  PRICE_ID: "price_1TXKFw8UGolkm4dx099e5Fjd", // $29/month
  PRICE_ID_ANNUAL: "price_1TXKGf8UGolkm4dxpCslRxw2", // $290/year (~$24.17/mo, save $58)
  LOOKUP_KEY: "solo_pro_plus_monthly",
  PRICE_ID_EUR_MONTHLY: "TODO_solo_pro_plus_eur_monthly", // €29/mo
  PRICE_ID_EUR_ANNUAL: "TODO_solo_pro_plus_eur_annual", // €290/yr
  PRICE_ID_GBP_MONTHLY: "TODO_solo_pro_plus_gbp_monthly", // £25/mo
  PRICE_ID_GBP_ANNUAL: "TODO_solo_pro_plus_gbp_annual", // £250/yr
} as const;

export type SoloArtistTier = "free" | "booker" | "pro" | "pro_plus" | "shop_member";

export interface SoloArtistSubscriptionStatus {
  isActive: boolean;
  subscriptionId?: string;
  currentPeriodEnd?: string;
  isMemberOfShop: boolean;
  hasAccess: boolean; // true always now (free tier gets access)
  tier: SoloArtistTier;
}

export function useSoloArtistSubscription() {
  const { user, session, memberships, membershipsLoaded } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { countryCode } = useLocale();

  // Check solo artist subscription status
  const { data: subscriptionStatus, isLoading, refetch } = useQuery({
    queryKey: ["solo-artist-subscription", user?.id],
    queryFn: async (): Promise<SoloArtistSubscriptionStatus> => {
      // Check if user is member of any shop
      const isMemberOfShop = memberships.length > 0;

      // If user is member of a shop, they have free access (no need to check subscription)
      if (isMemberOfShop) {
        return {
          isActive: false,
          isMemberOfShop: true,
          hasAccess: true,
          tier: "shop_member" as SoloArtistTier,
        };
      }

      // Check Stripe for solo artist subscription
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;

      // Find an active Solo Pro subscription (current US, legacy, or
      // EUR variants).
      const proSub = data?.subscriptions?.find(
        (sub: any) =>
          sub.productId === SOLO_ARTIST.PRODUCT_ID ||
          sub.productId === SOLO_ARTIST.EUR_PRODUCT_ID ||
          sub.productId === SOLO_ARTIST.EUR_PRODUCT_ANNUAL_ID ||
          SOLO_ARTIST.LEGACY_PRODUCT_IDS.includes(sub.productId),
      );

      // Find an active Solo Pro Plus subscription. Pro Plus is the
      // "guest chair" upgrade tier ($29/mo).
      const proPlusSub = data?.subscriptions?.find(
        (sub: any) => sub.productId === SOLO_PRO_PLUS.PRODUCT_ID,
      );

      // Find an active Solo Booker subscription separately.
      const bookerSub = data?.subscriptions?.find(
        (sub: any) => sub.productId === SOLO_BOOKER.PRODUCT_ID,
      );

      // Tier precedence: Pro Plus > Pro > Booker > Free. Higher-paying
      // tiers win if (somehow) multiple are active on the same account.
      const activeSub = proPlusSub || proSub || bookerSub;
      const tier: SoloArtistTier = proPlusSub
        ? "pro_plus"
        : proSub
        ? "pro"
        : bookerSub
        ? "booker"
        : "free";

      return {
        isActive: !!activeSub,
        subscriptionId: activeSub?.id,
        currentPeriodEnd: activeSub?.currentPeriodEnd,
        isMemberOfShop: false,
        hasAccess: true, // Always true now - free tier gets access
        tier,
      };
    },
    enabled: !!user && !!session && membershipsLoaded,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Create checkout for a solo artist subscription. `tier` selects
  // which Stripe price to charge against -- 'pro' is the original
  // \$19/mo Solo Artist Pro, 'booker' is the new \$9/mo Solo Booker.
  // Defaults to 'pro' for backward compatibility with existing
  // call sites.
  const createCheckout = useMutation({
    mutationFn: async ({
      billingInterval = "monthly",
      promoCode,
      tier = "pro",
    }: {
      billingInterval?: "monthly" | "annual";
      promoCode?: string;
      tier?: "pro" | "booker" | "pro_plus";
    } = {}) => {
      const { data, error } = await supabase.functions.invoke("create-solo-artist-checkout", {
        body: { billingInterval, promoCode: promoCode || undefined, tier, country: countryCode },
      });
      if (error) throw error;
      return data as { url: string };
    },
    onSuccess: (data) => {
      if (data.url) {
        // Same-tab redirect — see useSubscription.ts for the full
        // explanation. window.open() called from this onSuccess (after
        // the create-solo-artist-checkout fetch resolves) is silently
        // blocked by browser popup blockers because the user-gesture
        // context has expired by the time the network round-trip
        // finishes.
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Checkout Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update an existing solo-artist subscription to a different tier
  // (Booker / Pro / Pro Plus) without creating a second sub. Uses
  // Stripe's subscriptions.update with proration_behavior=create_prorations,
  // so the user pays a prorated balance for the rest of the period
  // and the old sub is replaced cleanly.
  //
  // Only useful for users who ALREADY have an active solo sub. If
  // they're on Free, use createCheckout instead — there's no
  // subscription to update.
  const updateSubscription = useMutation({
    mutationFn: async ({
      tier,
      billingInterval = "monthly",
    }: {
      tier: "pro" | "booker" | "pro_plus";
      billingInterval?: "monthly" | "annual";
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "update-solo-artist-subscription",
        { body: { tier, billingInterval } },
      );
      if (error) throw error;
      return data as { success: boolean; subscriptionId: string; newPriceId: string; changed: boolean };
    },
    onSuccess: () => {
      // Refetch subscription state so the new tier flips UI immediately.
      queryClient.invalidateQueries({ queryKey: ["solo-artist-subscription"] });
      toast({
        title: "Plan updated",
        description: "Your subscription has been switched. The prorated charge will appear on your next invoice.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't update plan",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Open customer portal for subscription management
  const openCustomerPortal = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      return data as { url: string };
    },
    onSuccess: (data) => {
      if (data.url) {
        // Same popup-blocker fix as createCheckout above.
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Portal Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    subscriptionStatus,
    isLoading: isLoading || !membershipsLoaded,
    refetch,
    isActive: subscriptionStatus?.isActive ?? false,
    isMemberOfShop: subscriptionStatus?.isMemberOfShop ?? memberships.length > 0,
    hasAccess: true, // Always true now - free tier gets access
    tier: subscriptionStatus?.tier ?? (memberships.length > 0 ? "shop_member" : "free") as SoloArtistTier,
    createCheckout,
    updateSubscription,
    openCustomerPortal,
  };
}
